<template>
  <div id="app" class="min-h-screen bg-gray-50">
    <nav v-if="authStore.isAuthenticated" class="bg-white shadow-sm border-b border-gray-200">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <div class="flex items-center">
            <router-link 
              :to="authStore.getDefaultRoute()" 
              class="flex items-center"
            >
              <h1 class="text-xl font-bold text-primary-600">MERCH SHOP</h1>
            </router-link>
          </div>
          
          <div class="flex items-center space-x-4">
            <!-- Главная и логотипы клиента или кнопка "Товары" -->
            <div v-if="authStore.user?.role === 'client' || authStore.user?.role === 'vip_client'" class="flex items-center space-x-4">
              <!-- Главная -->
              <router-link 
                to="/" 
                class="text-sky-600 hover:text-primary-600 px-3 py-2 rounded-md text-base font-medium"
              >
                Главная
              </router-link>
              
              <!-- Кнопка "Товары" (только если нет логотипов) -->
              <router-link 
                v-if="clientLogos.length === 0"
                to="/products" 
                class="text-sky-600 hover:text-primary-600 px-3 py-2 rounded-md text-base font-medium"
              >
                Товары
              </router-link>
              
              <!-- Логотипы клиента -->
              <router-link
                v-for="logo in clientLogos"
                :key="logo.id"
                :to="`/products?logo_id=${logo.id}`"
                class="text-sky-600 hover:text-primary-600 px-3 py-2 rounded-md text-base font-medium"
              >
                {{ logo.name }}
              </router-link>
            </div>
            
            <router-link 
              v-if="authStore.user?.role === 'client' || authStore.user?.role === 'vip_client'" 
              to="/orders" 
              class="text-sky-600 hover:text-primary-600 px-3 py-2 rounded-md text-base font-medium"
            >
              Личный кабинет
            </router-link>
            
            <router-link 
              v-if="authStore.user?.role === 'manager'" 
              to="/manage" 
              class="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium"
            >
              Управление
            </router-link>
            
            <router-link 
              v-if="authStore.user?.role === 'admin'" 
              to="/admin" 
              class="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium"
            >
              Администрирование
            </router-link>
            
            <div class="flex items-center space-x-2">
              <!-- Индикатор корзины для клиентов -->
              <div v-if="authStore.user?.role === 'client' || authStore.user?.role === 'vip_client'" class="flex items-center space-x-2">
                <router-link 
                  to="/cart" 
                  class="relative text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium"
                >
                  <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
                  </svg>
                  <span
                    v-if="cartStore.totalPositions > 0"
                    class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center"
                  >
                    {{ cartStore.totalPositions }}
                  </span>
                </router-link>
                <span v-if="!cartStore.isEmpty" class="text-sm text-gray-600">{{ formatPrice(cartStore.totalAmount) }} ₽</span>
              </div>


              <button
                @click="logout" 
                class="text-sky-600 hover:text-red-600 px-3 py-2 rounded-md text-base font-medium"
              >
                Выйти
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
    
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
      <router-view />
    </main>
    
    <!-- Футер с информацией о логотипах -->
    <footer v-if="authStore.isAuthenticated && (authStore.user?.role === 'client' || authStore.user?.role === 'vip_client')" class="fixed bottom-0 left-0 right-0 bg-gray-50 border-t border-gray-200 py-2 z-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col items-start space-y-1">
          <div>
            <router-link 
              to="/orders" 
              class="text-gray-600 hover:text-primary-600 text-xs underline font-medium"
            >
              Личный кабинет
            </router-link>
          </div>
          <div class="text-xs text-gray-500">
            Правообладание 2026 {{ clientLogosText }}, все права защищены.
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { useAuthStore } from './stores/auth'
import { useRouter } from 'vue-router'
import { useCartStore } from './stores/cart'
import { ref, onMounted, watch, computed } from 'vue'
import { logosAPI, balanceAPI } from './services/api'

const authStore = useAuthStore()
const router = useRouter()
const cartStore = useCartStore()

// Состояние для логотипов клиента
const clientLogos = ref([])

// Баланс для VIP РОП и Менеджер-клиентов
const vipBalance = ref(null)

// Проверяет наличие активной VIP роли у пользователя
const hasActiveVipRole = (roleName) => {
  return authStore.user?.vip_roles?.some(r => r.role === roleName && r.is_active) || false
}

const fetchVipBalance = async () => {
  const role = authStore.user?.role
  const isRop = role === 'vip_rop' || hasActiveVipRole('vip_rop')
  const isManager = role === 'vip_manager_client' || hasActiveVipRole('vip_manager_client')
  if (isRop || isManager) {
    try {
      const res = await balanceAPI.getBalance()
      vipBalance.value = res.data.balance
    } catch {
      vipBalance.value = null
    }
  } else {
    vipBalance.value = null
  }
}

// Вычисляемое свойство для текста логотипов
const clientLogosText = computed(() => {
  if (clientLogos.value.length === 0) return ''
  return clientLogos.value.map(logo => logo.name).join(', ')
})

const logout = () => {
  authStore.logout()
  router.push('/login')
}

const formatPrice = (price) => {
  return Number(price).toLocaleString('ru-RU')
}

// Загрузка логотипов клиента
const fetchClientLogos = async () => {
  if ((authStore.user?.role === 'client' || authStore.user?.role === 'vip_client') && authStore.user?.id) {
    try {
      // Для VIP клиента нужно получить parent_vip_client_id из vip_roles
      let clientId = authStore.user.id
      
      if (authStore.user?.role === 'vip_client' && authStore.user.vip_roles) {
        // Ищем роль с parent_vip_client_id
        const vipRole = authStore.user.vip_roles.find(role => 
          role.is_active && role.parent_vip_client_id
        )
        
        if (vipRole && vipRole.parent_vip_client_id) {
          // Используем parent_vip_client_id для получения логотипов
          clientId = vipRole.parent_vip_client_id
        }
      }
      
      const response = await logosAPI.getClientLogos(clientId)
      clientLogos.value = response.data
    } catch (error) {
      console.error('Ошибка загрузки логотипов:', error)
      clientLogos.value = []
    }
  }
}

// Отслеживание изменений пользователя
watch(() => authStore.user, (newUser) => {
  if (newUser?.role === 'client' || newUser?.role === 'vip_client') {
    fetchClientLogos()
  } else {
    clientLogos.value = []
  }
  fetchVipBalance()
}, { immediate: true, flush: 'post' })

// Загрузка логотипов при монтировании
onMounted(async () => {
  try {
    if (authStore.user?.role === 'client' || authStore.user?.role === 'vip_client') {
      await fetchClientLogos()
    }
    await fetchVipBalance()
  } catch (error) {
    console.error('Ошибка загрузки логотипов в App:', error)
  }
  // Обновление баланса при удалении/изменении заказа
  window.addEventListener('vip-balance-update', fetchVipBalance)
})
</script>
